import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plus, Eye, Edit2, Trash2, AlertCircle, MapPin, Grid3X3 } from 'lucide-react';
import { locationsAPI } from '../../services/garageApi';
import ZoneForm from '../zones/ZoneForm';

const Locations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;
  const [showZoneForm, setShowZoneForm] = useState(false);
  const [zoneFormLocationId, setZoneFormLocationId] = useState(null);

  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1); // Reset to first page when search changes
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const fetchLocations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        search: debouncedSearch,
        page: currentPage,
        per_page: itemsPerPage
      };

      const response = await locationsAPI.getAll(params);

      const locationsData = response?.data || [];

      if (!Array.isArray(locationsData)) {
        throw new Error('Invalid response format');
      }

      setLocations(locationsData);

      // Handle pagination metadata
      if (response.pagination) {
        setTotalPages(response.pagination.last_page || 1);
        setTotalItems(response.pagination.total || locationsData.length);
      } else {
        // Fallback if no pagination info
        setTotalPages(1);
        setTotalItems(locationsData.length);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      setError('Failed to load locations. Please try again.');
      setLocations([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, currentPage]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const handleDelete = useCallback(async (id) => {
    const location = locations.find(l => l.id === id);
    const locationName = location?.name || `Location #${id}`;

    if (!window.confirm(`Are you sure you want to delete ${locationName}? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeleting(id);
      await locationsAPI.delete(id);
      setLocations(prev => prev.filter(location => location.id !== id));
    } catch (error) {
      console.error('Error deleting location:', error);
      setError('Failed to delete location. Please try again.');
      fetchLocations();
    } finally {
      setDeleting(null);
    }
  }, [locations, fetchLocations]);

  const handleSearchChange = useCallback((e) => {
    setSearch(e.target.value);
  }, []);

  const handleRetry = useCallback(() => {
    fetchLocations();
  }, [fetchLocations]);

  const handleAddZone = useCallback((locationId) => {
    setZoneFormLocationId(locationId);
    setShowZoneForm(true);
  }, []);

  const handleZoneFormSuccess = useCallback(() => {
    setShowZoneForm(false);
    setZoneFormLocationId(null);
    fetchLocations();
  }, [fetchLocations]);

  const handleZoneFormCancel = useCallback(() => {
    setShowZoneForm(false);
    setZoneFormLocationId(null);
  }, []);

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const goToPrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const hasNext = currentPage < totalPages;
  const hasPrevious = currentPage > 1;

  const emptyState = useMemo(() => (
      <tr>
        <td colSpan="5" className="px-6 py-12 text-center">
          <div className="text-gray-500">
            <p className="text-lg font-medium">No locations found</p>
            {search && (
                <p className="text-sm mt-1">
                  Try adjusting your search terms or{' '}
                  <button
                      onClick={() => setSearch('')}
                      className="text-blue-600 hover:text-blue-800 underline"
                  >
                    clear search
                  </button>
                </p>
            )}
          </div>
        </td>
      </tr>
  ), [search]);

  if (loading && locations.length === 0) {
    return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading locations...</span>
        </div>
    );
  }

  return (
      <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Locations</h1>
            <p className="text-gray-600">Manage storage locations</p>
          </div>
          <Link
              to="/locations/create"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Location
          </Link>
        </div>

        {/* Error Message */}
        {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                  <button
                      onClick={handleRetry}
                      className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
                  >
                    Try again
                  </button>
                </div>
              </div>
            </div>
        )}

        {/* Locations Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          {/* Search Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Locations
              </h3>
              <div className="relative max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                    type="text"
                    value={search}
                    onChange={handleSearchChange}
                    placeholder="Search locations..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Zones
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
              {locations.length > 0 ? (
                  locations.map((location) => (
                      <tr key={location.id} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center">
                                <MapPin className="h-5 w-5 text-purple-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {location.name || 'N/A'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {location.code || 'No code'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                location.type === 'warehouse'
                                    ? 'bg-blue-100 text-blue-800'
                                    : location.type === 'shelf'
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-100 text-gray-800'
                            }`}>
                              {location.type || 'General'}
                            </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Link
                            to={`/zones?location=${location.id}`}
                            className="inline-flex items-center gap-1 text-sm text-gray-700 hover:text-blue-600 transition-colors"
                          >
                            <Grid3X3 className="h-4 w-4 text-gray-400" />
                            <span className="font-medium">{location.zones_count ?? 0}</span>
                          </Link>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs truncate">
                            {location.description || 'No description'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <Link
                                to={`/locations/${location.id}`}
                                className="group inline-flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 transform hover:scale-110"
                                title="View Location"
                            >
                              <Eye className="h-4 w-4" />
                            </Link>

                            <Link
                                to={`/locations/${location.id}/edit`}
                                className="group inline-flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:text-green-600 hover:bg-green-50 transition-all duration-200 transform hover:scale-110"
                                title="Edit Location"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Link>

                            <button
                                onClick={() => handleAddZone(location.id)}
                                className="group inline-flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:text-purple-600 hover:bg-purple-50 transition-all duration-200 transform hover:scale-110"
                                title="Add Zone"
                            >
                              <Plus className="h-4 w-4" />
                            </button>

                            <button
                                onClick={() => handleDelete(location.id)}
                                disabled={deleting === location.id}
                                className="group inline-flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all duration-200 transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                                title="Delete Location"
                            >
                              {deleting === location.id ? (
                                  <div className="animate-spin h-4 w-4 border-2 border-red-600 border-t-transparent rounded-full" />
                              ) : (
                                  <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                  ))
              ) : (
                  emptyState
              )}
              </tbody>
            </table>
          </div>

          {/* Loading overlay */}
          {loading && locations.length > 0 && (
              <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={goToPrevious}
                  disabled={!hasPrevious}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={goToNext}
                  disabled={!hasNext}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of{' '}
                    <span className="font-medium">{totalItems}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={goToPrevious}
                      disabled={!hasPrevious}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => goToPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={goToNext}
                      disabled={!hasNext}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showZoneForm && (
        <ZoneForm
          zone={null}
          locations={locations}
          defaultLocationId={zoneFormLocationId}
          onSuccess={handleZoneFormSuccess}
          onCancel={handleZoneFormCancel}
        />
      )}
      </>
  );
};

export default Locations;